/**
 * @file AdminMyClients.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useCallback, useEffect, useState } from "react";
import Widget from "../../widgets/Widget";
import SelectInput from "../../../../components/forms/SelectInput";
import SearchInput from "../../../../components/forms/SearchInput";
import { useForm } from "../../../../utils/useForm";
import Request from "../../../../utils/fetchUtils";
import DgButton from "../../../../components/DgButton";
import DgModal from "../../../../components/DgModal";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import { useNavigate } from "react-router";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BlockIcon from '@mui/icons-material/Block';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { getInitialEquityFromHistorical } from "../../../../utils/utils";

const AdminMyClients = ({}) => {
  const navigate = useNavigate();
  const [filters, setFilters, resetFilters] = useForm({
  assigned: "",
  advisor: "",
  search: "",
  });
  const [selectedCustomer, setSelectedCustomer] = useState({
    modalAssignmentState: false,
    modalInactivateState: false,
    modalReassignmentState: false,
    customer: null
  });
  const [assignmentData, setAssignmentData, resetAssignmentData] = useForm({
    advisorKey: "",
    search: "",
  });
  const [reassignmentData, setReassignmentData, resetReassignmentData] = useForm({
    advisorKey: "",
    search: "",
  });
  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [advisors, setAdvisors] = useState({
    array: [],
    object: {},
  });
  const [customerToInactivate, setCustomerToInactivate] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

const filterData = () => {
    const { assigned, advisor, search } = filters;
    let filtered = [...allCustomers];

    if (assigned !== "") {
      filtered = filtered.filter((c) =>
        assigned === "1"
          ? c.advisorOwner && c.advisorOwner !== ""
          : !c.advisorOwner || c.advisorOwner === ""
      );
    }

    if (advisor !== "") {
      filtered = filtered.filter((c) => c.advisorOwner === advisor);
    }

    if (search && search.trim() !== "") {
      const query = search.toLowerCase();
      filtered = filtered.filter((c) =>
        c.fullName?.toLowerCase().includes(query)
      );
    }

    setCustomers(filtered);
  };
  
  const handleResetFilters = () => {
    resetFilters();
    setCustomers(allCustomers);
  };


  const handleSelectCustomerForAssignment = (customer) => {
    resetAssignmentData();
    resetReassignmentData();
    setSelectedCustomer({
      modalAssignmentState: true,
      modalInactivateState: false,
      modalReassignmentState: false,
      customer: customer
    });
  };

  const handleSelectCustomerForBrokers = (customer) => {
    setSelectedCustomer({
      modalAssignmentState: false,
      modalInactivateState: false,
      modalReassignmentState: false,
      customer: customer
    });
  };

  const generateAuthorizeUrl = () => {
    const scope = "read,trade,market";
    const { REACT_APP_TRADIER_CLIENT_ID, REACT_APP_TRADIER_CLIENT_SC } =
      process.env;
    return `https://api.tradier.com/v1/oauth/authorize?client_id=${REACT_APP_TRADIER_CLIENT_ID}&scope=${scope}&state=${REACT_APP_TRADIER_CLIENT_SC}`;
  };

  const fetchCustomers = async () => {
    const res = await Request("/trading/my-clients");
    if (res.ok) {
      // ordenar por nombre alfabéticamente
      const sortedCustomers = res.data.sort((a, b) => {
        const nameA = a.fullName?.toLowerCase() || "";
        const nameB = b.fullName?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      });

      setCustomers(sortedCustomers);
      setAllCustomers(sortedCustomers);
    }
  };


  const fetchAdvisors = async () => {
    const res = await Request("/user?role=advisor");
    if (res.ok) {
      const objectAdvisors = {};
      res.data.forEach((advisor) => {
        objectAdvisors[advisor.SK] = advisor;
      });
      setAdvisors({ array: res.data, object: objectAdvisors });
    }
  };

  const postAdvisorAssignment = async () => {
    if (!selectedCustomer.customer || !selectedCustomer.customer.SK) {
      Swal.fire("Error", "Cliente no válido o información incompleta", "error");
      return;
    }
    
    if (!assignmentData.advisorKey) {
      Swal.fire("Error", "Please select an advisor", "error");
      return;
    }
    
    const res = await Request(
      "/trading/my-clients",
      {
        customerKey: selectedCustomer.customer.SK,
        advisorKey: assignmentData.advisorKey,
      },
      "POST"
    );
    if (res.ok) {
      Swal.fire("Success", "User successfully assigned", "success").then(() => {
        fetchCustomers();
        resetFilters();
        resetAssignmentData();
      });
    } else {
      Swal.fire("Something happened ...", res.error, "error");
    }
    setSelectedCustomer({
      modalAssignmentState: false,
      modalInactivateState: false,
      modalReassignmentState: false,
      customer: null
    });
  };  
  
  const handleReassignAdvisor = async () => {
    if (!reassignmentData.advisorKey) {
      Swal.fire("Error", "Please select a new advisor", "error");
      return;
    }
    
    if (!selectedCustomer.customer || !selectedCustomer.customer.SK) {
      Swal.fire("Error", "Cliente no válido o información incompleta", "error");
      return;
    }
    
    if (reassignmentData.advisorKey === selectedCustomer.customer.advisorOwner) {
      Swal.fire("Error", "Please select a different advisor than the current one", "error");
      return;
    }
    
    try {
      const res = await Request(
        "/user/reassign-advisor",
        {
          clientKey: selectedCustomer.customer.SK,
          advisorKey: reassignmentData.advisorKey,
        },
        "POST"
      );
      
      if (res.ok) {
        Swal.fire("Success", "Advisor successfully reassigned", "success").then(() => {
          fetchCustomers();
          resetReassignmentData();
          setSelectedCustomer({
            modalAssignmentState: false,
            modalInactivateState: false,
            modalReassignmentState: false,
            customer: null
          });
        });
      } else {
        Swal.fire("Error", res.message || "Failed to reassign advisor", "error");
      }
    } catch (error) {
      console.error("Error during reassignment:", error);
      Swal.fire("Error", "An unexpected error occurred during reassignment", "error");
    }
  };

  const openReassignModal = (customer) => {
    if (!customer) {
      console.error("Customer object is undefined in openReassignModal");
      return;
    }
    
    resetReassignmentData();
    resetAssignmentData();
    
    setSelectedCustomer({
      modalAssignmentState: false,
      modalInactivateState: false,
      modalReassignmentState: true,
      customer: customer
    });
  };

  const disableReassignButton = () => {
    return !reassignmentData.advisorKey || 
           !selectedCustomer.customer || 
           !selectedCustomer.customer.SK || 
           reassignmentData.advisorKey === selectedCustomer.customer.advisorOwner;
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    await fetchCustomers();
    await fetchAdvisors();
    setIsLoading(false);
  }, []);

  const handleViewClientDetails = (customer) => {
    const userRoute = `/dashboard/customer/${customer.SK}`;
    navigate(userRoute, { state: customer });
  };

  const getPerformance = (historical) => {
    const initialEquity = getInitialEquityFromHistorical(historical);
    if (historical) {
      return parseFloat(
        (
          historical[historical.length - 1]?.totalEquity - initialEquity
        ).toFixed(2)
      );
    } else {
      return "-";
    }
  };

  const openInactivateModal = (customer) => {
    setCustomerToInactivate(customer);
    setSelectedCustomer({
      modalAssignmentState: false,
      modalInactivateState: true,
      modalReassignmentState: false,
      customer: customer
    });
  };

  const handleInactivateUser = async () => {
    if (customerToInactivate) {
      setIsLoading(true);
      const res = await Request(`user/${customerToInactivate.userId}`, {}, "DELETE", {
        Userkey: customerToInactivate.SK,
      });
      setIsLoading(false);
      if (res.ok) {
        Swal.fire("Success", "User successfully inactivated", "success").then(() => {
          fetchData();
          setSelectedCustomer({
            modalAssignmentState: false,
            modalInactivateState: false,
            modalReassignmentState: false,
            customer: null
          });
        });
      } else {
        Swal.fire("Error", res.message || "We couldn't inactivate the user", "error");
      }
    }
  };
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    filterData();
  }, [filters, filterData]);

  return (
    <div>
      <Widget name={"my-clients"} width={"full-width"}>
        <div className="toppers">
          <div className="filters">
            <div>
              <div className="filter-title form-field">
                <SelectInput
                  customInput={{
                    label: "Filter by assigned",
                    name: "assigned",
                    value: filters.assigned,
                    action: setFilters,
                  }}
                  data={[
                    { value: "", text: "All" },
                    { value: "1", text: "Assigned" },
                    { value: "0", text: "Non assigned" },
                  ]}
                />
              </div>
            </div>
            <div>
              <div className="filter-title form-field">
                <SelectInput
                  customInput={{
                    label: "Filter by advisor",
                    name: "advisor",
                    value: filters.advisor,
                    action: setFilters,
                  }}
                  data={[
                    { value: "", text: "Advisor" },
                    ...advisors.array.map((adv) => ({
                      value: adv.SK,
                      text: adv.fullName,
                    })),
                  ]}
                />
              </div>
            </div>
            <div>
              <div className="filter-title form-field">                
                <SearchInput
                  customInput={{
                    label: "Search by name",
                    name: "search",
                    value: filters.search, 
                    action: setFilters,
                    tooltipBool: true,
                    tooltipText: "Search for a client by name",
                    placeholder: "Type a name...",
                  }}
                />
              </div>
            </div>
            <div>              
              <button 
                onClick={handleResetFilters}
                className="reset-filters-btn"
              >
                Reset Filters
              </button>
            </div>
          </div>

        </div>
        <table className="clients-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Score</th>
              <th>Performance</th>
              <th># Strategies</th>
              <th>% Allocated</th>
              <th>Advisor</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td>
                  <Loader />
                </td>
              </tr>
            ) : (
              <>
                {customers.length > 0 ? (
                  customers.map((customer) => (
                    <tr key={customer.userId}>
                      <td className="name">
                        <div className="user">
                          <div>
                            <span className="name">{customer.fullName}</span>
                            <span className="activity">
                              Last activity: {customer.updatedAt}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="score">
                        <p className="score-text">({customer.score || 0})</p>
                      </td>
                      <td className="performance">
                        <span
                          style={{
                            color:
                              getPerformance(customer.historical) > 0
                                ? "#ff5e5b"
                                : "#75a180",
                          }}
                        >
                          $ {getPerformance(customer.historical)}
                        </span>
                      </td>
                      <td className="strategy">
                        <span className="strategy-title">
                          {customer.strategies?.length || 0}
                        </span>
                      </td>
                      <td className="account">
                        {customer.usedPercentage !== undefined &&
                          customer.usedPercentage !== null
                          ? `${customer.usedPercentage}%`
                          : customer.usedPercentage}
                      </td>
                      <td className="account">
                        {customer.advisorOwner
                          ? advisors.object[customer.advisorOwner]?.fullName
                          : "Not assigned"}
                      </td>                      <td className="fees">
                        {(!customer.advisorOwner ||
                          customer.advisorOwner === "") ? (
                          <Tooltip title="Assign Advisor" placement="top-start">
                            <IconButton
                              onClick={() =>
                                handleSelectCustomerForAssignment(customer)
                              }
                            >
                              <PersonAddIcon style={{ color: "white" }} />
                            </IconButton>
                          </Tooltip>                        ) : (
                          <Tooltip title="Reassign Advisor" placement="top-start">
                            <IconButton
                              onClick={() => openReassignModal(customer)}
                            >
                              <TransferWithinAStationIcon style={{ color: "#ABECD6" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {customer && customer?.brokerAccounts ? (
                          <Tooltip title="View Details" placement="top-start">
                            <IconButton
                              onClick={() => handleViewClientDetails(customer)}
                            >
                              <VisibilityIcon sx={{ color: "white" }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}                        
                        <Tooltip title="Inactivate User" placement="top-start">
                          <IconButton
                            onClick={() => openInactivateModal(customer)}
                          >
                            <BlockIcon style={{ color: "white" }} />
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7}>No registered customers</td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </Widget>
      <DgModal
        modalState={selectedCustomer.modalAssignmentState}
        handleModalState={() => {
          if (selectedCustomer.modalAssignmentState) {
            resetAssignmentData();
            setSelectedCustomer({
              ...selectedCustomer,
              modalAssignmentState: false,
              customer: selectedCustomer.customer
            });
          } else {
            setSelectedCustomer({
              ...selectedCustomer,
              modalAssignmentState: true,
            });
          }
        }}
      >
        <h1 className="title">Assign advisor</h1>
        <div className="strategy-content">
          <p style={{ marginBottom: '20px', color: '#95B2C5', textAlign: 'center' }}>
            You are about to assign an advisor to this client:<br/>
            <strong>{selectedCustomer.customer?.fullName || "Cliente seleccionado"}</strong>
          </p>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Advisor",
                name: "advisorKey",
                value: assignmentData.advisorKey,
                action: setAssignmentData,
              }}
              data={[
                { value: "", text: "Choose an advisor" },
                ...advisors.array.map((adv) => ({
                  value: adv.SK,
                  text: adv.fullName,
                })),
              ]}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <DgButton
            category="secondary"
            size="large"
            text="Cancel"
            action={() => {
              resetAssignmentData();
              setSelectedCustomer({
                ...selectedCustomer,
                modalAssignmentState: false,
              });
            }}
          />
          <DgButton
            category="primary"
            size="large"
            text="Assign"
            action={postAdvisorAssignment}
            disabled={!assignmentData.advisorKey}
          />
        </div>
      </DgModal>
      <DgModal
        modalState={selectedCustomer.modalReassignmentState}
        handleModalState={() => {
          setSelectedCustomer({
            ...selectedCustomer,
            modalReassignmentState: !selectedCustomer.modalReassignmentState,
          });
        }}
      >        <h1 className="title">Reassign Advisor</h1>        <div className="strategy-content">
          <p style={{ marginBottom: '20px', color: '#95B2C5', textAlign: 'center' }}>
            You are about to reassign an advisor to this client:<br/>
            <strong>{selectedCustomer.customer?.fullName || "Cliente seleccionado"}</strong><br/>
            <span>
              Current advisor: {(selectedCustomer.customer?.advisorOwner && advisors.object[selectedCustomer.customer.advisorOwner]) 
                ? advisors.object[selectedCustomer.customer.advisorOwner].fullName 
                : "Not assigned"}
            </span>
          </p><div className="form-field">
            <SelectInput
              customInput={{
                label: "New Advisor",
                name: "advisorKey",
                value: reassignmentData.advisorKey,
                action: setReassignmentData,
              }}
              data={[
                { value: "", text: "Choose a new advisor" },
                ...advisors.array.map((adv) => ({
                  value: adv.SK,
                  text: adv.fullName,
                  disabled: selectedCustomer.customer && adv.SK === selectedCustomer.customer.advisorOwner,
                })),
              ]}
            />
            {selectedCustomer.customer && reassignmentData.advisorKey === selectedCustomer.customer.advisorOwner && (
              <p style={{ color: '#ff5e5b', fontSize: '12px', marginTop: '5px' }}>
                Please select a different advisor than the current one
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <DgButton
            category="secondary"
            size="large"
            text="Cancel"
            action={() => setSelectedCustomer({
              ...selectedCustomer,
              modalReassignmentState: false,
            })}
          />
          <DgButton
            category="primary"
            size="large"
            text="Reassign"
            action={handleReassignAdvisor}
            disabled={disableReassignButton()}
          />
        </div>
      </DgModal>
      <DgModal
        modalState={selectedCustomer.modalInactivateState}
        handleModalState={() => {
          setSelectedCustomer({
            ...selectedCustomer,
            modalInactivateState: !selectedCustomer.modalInactivateState,
          });
        }}
      >
        <h1 className="title">Inactivar Usuario</h1>
        <div className="strategy-content">
          <p style={{ marginBottom: '20px', color: '#95B2C5', textAlign: 'center' }}>
            Are you sure you want to inactivate the user?<br/>
            <strong>{customerToInactivate?.fullName}</strong><br/>
            <span style={{ fontSize: '14px', color: '#FF5E5B' }}>
              This action cannot be undone.
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <DgButton
            category="secondary"
            size="large"
            text="Cancel"
            action={() => setSelectedCustomer({
              ...selectedCustomer,
              modalInactivateState: false,
            })}
          />
          <DgButton
            category="danger"
            size="large"
            text="Inactivate"
            action={handleInactivateUser}
            style={{ backgroundColor: '#FF5E5B', color: 'white', border: '1px solid #FF5E5B' }}
          />
        </div>
      </DgModal>
    </div>
  );
};

AdminMyClients.propTypes = {};

export default AdminMyClients;